import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useState, useRef } from 'react';
import { TextField } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FilePicker } from '@/components/FilePicker';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { useAsyncMutationDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { ReportFormat } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type UseUploadQuantAnalysisFileModalProps = {
  projectId: number | undefined;
  quantAnalysisId?: number;
};

export const useUploadQuantAnalysisFileModal = ({
  projectId,
  quantAnalysisId,
}: UseUploadQuantAnalysisFileModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>('');
  const queryClient = useQueryClient();

  // Use refs to store current values for mutation
  const fileRef = useRef<File | null>(null);
  const nameRef = useRef<string>('');

  // Update refs when state changes
  fileRef.current = file;
  nameRef.current = name;

  const toggleModal = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  const apiClient = useChatApiClient();

  const { mutateAsync: startUpload } = useAsyncMutationDownloader({
    mutationFn: async () => {
      const currentFile = fileRef.current;
      const currentName = nameRef.current;

      if (!projectId || !currentFile) {
        throw new Error('Project ID and file are required');
      }

      const response = await apiClient.quantAnalysis.uploadProjectQuantAnalysisFile({
        projectId,
        name: currentName,
        file: currentFile,
        quantAnalysisId,
      });

      return response.data;
    },
  });

  const handleFileSelected = useCallback((e: ChangeEvent<HTMLInputElement>) => {

    if (e.target.value.length === 0) {
      return;
    }

    const selectedFile = e.target.files?.[0];

    if (selectedFile) {
      setFile(selectedFile);
      fileRef.current = selectedFile; // Update ref immediately
    } else {
      console.log('No file found in files array');
    }
  }, []);

  const canSubmit = useMemo(() => {
    const result = quantAnalysisId ? !!file : (!!file && !!name);
    return result;
  }, [file, name, quantAnalysisId]);

  const handleUpload = useCallback(async () => {

    if (!canSubmit || !projectId || !file) {
      return;
    }

    // Determine format based on file extension
    const isJsonFile = file.name.toLowerCase().endsWith('.json');
    const format = isJsonFile ? ReportFormat.Word : ReportFormat.Excel;

    try {
      // Call startUpload and wait for it to complete
      await startUpload({
        meta: {
          format,
          name: file.name,
          title: `Uploading Analysis ${quantAnalysisId ? 'Version' : 'File'}`,
        },
      });

      // Only reset state after successful upload
      setIsOpen(false);
      setFile(null);
      setName('');
      fileRef.current = null;
      nameRef.current = '';

    } catch (error) {
      console.error('Upload failed:', error);
      // Don't reset state on error so user can try again
    }

    // Invalidate relevant queries to refresh data (equivalent to invalidateQuantPublicationStatus)
    if (quantAnalysisId) {
      queryClient.invalidateQueries({
        queryKey: ['project-quant-analysis', projectId, quantAnalysisId],
      });

      queryClient.invalidateQueries({
        queryKey: ['project-quant-analysis-versions', projectId, quantAnalysisId],
      });
    } else {
      // For new analysis uploads, invalidate the project query to update tabs
      queryClient.invalidateQueries({
        queryKey: ['get:projects', projectId],
      });

      // Also invalidate the list of analyses
      queryClient.invalidateQueries({
        queryKey: ['project-quant-analysis-list', projectId],
      });
    }
  }, [canSubmit, projectId, file, quantAnalysisId, startUpload, queryClient]);

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    nameRef.current = newName; // Update ref immediately
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setFile(null);
    setName('');
    // Reset refs when closing
    fileRef.current = null;
    nameRef.current = '';
  }, []);

  const UploadModalComponent = (
    <Modal
      open={isOpen}
      onClose={handleClose}
    >
      <Header text="Upload Analysis File" />

      <div style={{ padding: '24px' }}>
        {!quantAnalysisId && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Analysis Name
            </div>
            <TextField
              value={name}
              onChange={handleNameChange}
              placeholder="Enter analysis name"
              fullWidth
              variant="outlined"
              size="small"
            />
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <FilePicker
            accept=".xlsx,.xls,.json"
            onSelect={handleFileSelected}
          />
        </div>

        {file && (
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginTop: '8px',
            wordBreak: 'break-all',
          }}>
            {file.name}
          </div>
        )}
      </div>

      <ButtonSet>
        <Button.Destructive
          onClick={handleClose}
        >
          Cancel
        </Button.Destructive>

        <Button.Primary
          onClick={() => {
            handleUpload();
          }}
          disabled={!canSubmit}
        >
          Upload
        </Button.Primary>
      </ButtonSet>
    </Modal>
  );

  return [toggleModal, UploadModalComponent] as const;
};